import { featureEnabled } from '../../functions/featureFlags.js'
// SimoneSuggestions — THE COMPOSER'S FOCUS PANEL (DESIGN-COMPOSER-FOCUS-
// POPOVER-CONTEXT-INSPECT-1). Nika 2026-10-04: "redesign ai textarea focus
// popover, make it more beautiful and clear with more sensible options and
// option to choose/switch active context, based on what is active (app or
// page) and also option to inspect and ask".
//
//   Context                                             ← what the ask is ABOUT
//   [▣ Overview] [▢ Validators] [⌂ Solana] [▢ Tokens] [▢ Finances]
//   [⌖ Inspect & ask]  [≡ Summarize]  [▦ Build a board]  ← the three verbs
//   Suggested for Overview                              ← the context's asks
//     ✦ How is the Solana fleet doing right now?
//     △ What needs attention here?
//     ? What can you do here?
//   Recent                                              ← the member's own
//     ↺ why is this amber?
//   Mode  [✦|▦|≡|▢|▷]  Dashboard — Builds a board of widgets
//
// Every row comes from the shell (workspace functions/aiScope.js — the
// context model; functions/aiPanel.js — the rows, the verbs, the sends), so
// the panel builds no prompt and knows no app. A host without them (another
// app mounting the composer) mounts nothing.
//
// THE MATERIAL is the one popover glass (dropdowns spec §1 / §7.2): the
// launcher's `search-glass`, no line, the `overlay` elevation, the card
// radius — the focus panel and the search dropdown are one material, and the
// rows are THE house menu row (MenuPanelItem — Nika: "reuse this contextmenu
// style, shape and item styles to all other dropdowns"). Sections are
// separated by rhythm, never strokes; equal insets on every side. On a phone
// it is a sheet: the sheet radius, its own scroll, the 44 px rows (@coarse).
//
// WHEN IT PAINTS: the composer has focus (`_promptFocused`, mirrored by the
// field with a 250 ms blur grace so a press lands), no menu sits above it (a
// DropdownRoot layer, the composer's "+" menu — one panel at a time), it is
// not put away (`_aiPanel`, shared functions/aiPanelFocus.js) and no region
// is being inspected. While the assistant's thread or drawer holds the answer
// it steps aside — unless it was asked for (the context pill, ↑ in an empty
// field). While a turn streams the reasoning pill owns the strip.
//
// THE KEYBOARD: ↑ in an empty field opens it on the row nearest the field;
// ↑ ↓ Home End walk the suggestion rows (the menu), ← → the context chips
// and the verbs, ← → the mode segments (each a radio group or a row with its
// own model), Tab moves between sections, Enter runs the focused one, ONE
// Escape puts the panel away and hands focus back to the field. Motion is
// the house enter, one step faster out; the brand reset lands it at once
// under reduced motion.

export const SimoneSuggestions = {
  // FA204 / frank trap — helpers live in `scope`, reached via `el.scope.*`.
  scope: {
    // Route WITHOUT the multi-workspace-tabs `/w/<slug>` prefix — the hide
    // compares bare app routes. Inline mirror of workspacePrefix.js
    // stripWorkspacePrefix (same helper as ReasoningPill's).
    routePath(root) {
      const p =
        (root && root.currentPath) ||
        (typeof window !== 'undefined' ? window.location.pathname : '/')
      const stripped = String(p).replace(/^\/w\/[a-z0-9-]{1,48}(?=\/|$)/, '')
      return stripped === '' ? '/' : stripped
    },
    // The shell's panel model is registered on this host.
    hasModel(el) {
      const fns = el && el.context && el.context.functions
      return !!(fns && typeof fns.aiPanelSuggestions === 'function')
    },
    groups(el, s) {
      if (!this.hasModel(el)) return []
      try {
        const out = el.call('aiPanelSuggestions', s && s.root)
        return Array.isArray(out) ? out : []
      } catch (_) {
        return []
      }
    },
    actions(el, s) {
      if (!this.hasModel(el)) return []
      try {
        const out = el.call('aiPanelActions', s && s.root)
        return Array.isArray(out) ? out : []
      } catch (_) {
        return []
      }
    },
    // The chosen context's key — a row key carries it, so a context switch
    // re-mounts the rows instead of patching them in place.
    scopeKey(el, s) {
      try {
        return String(el.call('aiScopeChosenKey', s && s.root) || '')
      } catch (_) {
        return ''
      }
    },
    // A change signature of a group's rows (djb2 → base36), not a hash for
    // anything but telling two row lists apart.
    sig(rows) {
      const t = (rows || []).map((r) => (r && r.text) || '').join('|')
      let h = 5381
      for (let i = 0; i < t.length; i++) h = ((h << 5) + h + t.charCodeAt(i)) | 0
      return (h >>> 0).toString(36)
    }
  },

  // Declared subscriptions (FA515): every discriminator the hide / paint /
  // row factories read behind a short-circuit.
  subscribeTo: [
    'currentPath',
    'chatTyping',
    'activeProcesses',
    'assistantOpen',
    '_homeThreadOpen',
    'meetView',
    '_promptFocused',
    '_dropdownLayer',
    '_promptMenuOpen',
    '_aiPanel',
    '_aiInspect',
    '_aiAskContext',
    '_aiRefMutedFor',
    '_canvasSlot',
    'workspace',
    'workspaceApps',
    'workspaceProject',
    'assistantMode',
    'chatMessages',
    'sessions',
    'lang',
    'selectedTicket',
    'selectedEvent',
    'showEventDetail',
    'selectedDocument',
    'profileEmail',
    'selectedPerson',
    '_selectedAgent',
    '_selectedTeamMember',
    '_createAgentOpen',
    '_createMemberOpen',
    'selectedAnnouncement',
    'editorDoc',
    'data'
  ],

  hide: (el, s) => {
    // /flags gate: assistant off removes the panel everywhere.
    if (!featureEnabled(s.root, 'aiAssistant')) return true
    const r = s.root
    if (!r) return true
    if (!el.scope.hasModel(el)) return true
    // The composer's own route-hide: never over a page's own bottom UI.
    const path = el.scope.routePath(r)
    if (path === '/add-app' || path === '/chat' || path === '/meet-guest') return true
    if (path === '/meet' && r.meetView === 'call') return true
    // A streaming turn: the reasoning pill owns the strip (reading
    // activeProcesses also TOUCHES the signal, so the panel returns after).
    if (r.chatTyping || (r.activeProcesses || []).some((p) => p && p.status === 'running')) {
      return true
    }
    // The thread, the drawer or a Fly page (the workspace's fly canvas,
    // functions/assistantFly.js — its next asks are on the page) holds the
    // answer: step aside — unless asked for.
    const fns = el.context && el.context.functions
    const fly =
      fns && typeof fns.assistantFlyShown === 'function' ? !!el.call('assistantFlyShown', r) : false
    if ((r.assistantOpen || r._homeThreadOpen || fly) && r._aiPanel !== 'open') return true
    return false
  },

  role: 'dialog',
  ariaModal: 'false',
  ariaLabel: (el) => el.call('polyglot', 'ai.panel.label'),

  // ── THE PANEL — one glass surface at composer width; sections by rhythm.
  flow: 'y',
  gap: 'Z1',
  width: '100%',
  minWidth: '0',
  boxSizing: 'border-box',
  fontSize: 'Z1',
  theme: 'search-glass',
  borderWidth: '0',
  round: 'radiusCard',
  padding: 'Z',
  shadow: 'overlay',
  maxHeight: '70dvh',
  overflowY: 'auto',
  overscrollBehavior: 'contain',
  // DESIGN_SPEC 6.5: under reduced transparency the glass is the opaque
  // `dialog` surface, no blur.
  '@reduceTransparency': { theme: 'dialog', backdropFilter: 'none' },
  // A phone: the sheet — its radius, a taller cap, its own scroll.
  '@mobileL': { round: 'radiusSheet', maxHeight: '62dvh', padding: 'Z' },

  // PAINTS while focused, under no menu, not put away, not inspecting —
  // opacity + visibility + a 6 px rise, never display, so the motion can run;
  // no pointer while hidden. Inline in each factory (no scope helper), so a
  // reader may call them with any element.
  opacity: (el, s) =>
    s.root?._promptFocused &&
    !s.root._dropdownLayer &&
    !s.root._promptMenuOpen &&
    s.root._aiPanel !== 'dismissed' &&
    !s.root._aiInspect
      ? '1'
      : '0',
  visibility: (el, s) =>
    s.root?._promptFocused &&
    !s.root._dropdownLayer &&
    !s.root._promptMenuOpen &&
    s.root._aiPanel !== 'dismissed' &&
    !s.root._aiInspect
      ? 'visible'
      : 'hidden',
  pointerEvents: (el, s) =>
    s.root?._promptFocused &&
    !s.root._dropdownLayer &&
    !s.root._promptMenuOpen &&
    s.root._aiPanel !== 'dismissed' &&
    !s.root._aiInspect
      ? 'auto'
      : 'none',
  transform: (el, s) =>
    s.root?._promptFocused &&
    !s.root._dropdownLayer &&
    !s.root._promptMenuOpen &&
    s.root._aiPanel !== 'dismissed' &&
    !s.root._aiInspect
      ? 'none'
      : 'translateY(6px)',
  // Under a menu, put away or inspecting the panel goes AT ONCE (the next
  // layer is the surface now); otherwise the house enter / one-step-faster exit.
  transition: (el, s) =>
    s.root?._promptFocused &&
    (s.root._dropdownLayer ||
      s.root._promptMenuOpen ||
      s.root._aiPanel === 'dismissed' ||
      s.root._aiInspect)
      ? 'none'
      : s.root?._promptFocused
        ? 'opacity B defaultBezier, transform B defaultBezier, visibility 0s'
        : 'opacity A defaultBezier, transform A defaultBezier, visibility 0s A',

  // THE FOCUS MIRROR'S OTHER HALF (dropdowns spec §8): focus arriving here
  // keeps `_promptFocused` up (and cancels the field's pending clear); focus
  // leaving for anything but this panel or the field starts the same clear.
  // Host = PromptBand's parent, the composer card that holds the field's
  // `_blurT`.
  onFocusin: (e, el, s) => {
    const host = el.parent && el.parent.parent
    if (host && host._blurT) {
      clearTimeout(host._blurT)
      host._blurT = null
    }
    if (s.root && !s.root._promptFocused) s.root.update({ _promptFocused: true })
  },
  onFocusout: (e, el, s) => {
    const host = el.parent && el.parent.parent
    const next = e && e.relatedTarget
    if (!host || (next && el.node && el.node.contains(next))) return
    if (next && host.Textarea && next === host.Textarea.node) return
    if (host._blurT) clearTimeout(host._blurT)
    host._blurT = setTimeout(() => {
      host._blurT = null
      if (s.root && s.root._promptFocused) s.root.update({ _promptFocused: false })
    }, 250)
  },
  // ONE Escape puts the panel away and hands focus back to the field (one
  // Escape = one layer: it closes nothing else). ↑ ↓ Home End walk the
  // suggestion rows; the chips, the verbs and the mode segments keep their
  // own arrows.
  onKeydown: (e, el, s) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      e.stopPropagation()
      const host = el.parent && el.parent.parent
      const ta = host && host.Textarea && host.Textarea.node
      if (ta && typeof ta.focus === 'function') ta.focus()
      if (s && s.root && typeof s.root.update === 'function') {
        s.root.update({ _aiPanel: 'dismissed' }, { preventFetch: true })
      }
      return
    }
    const menu = el.PanelMenu && el.PanelMenu.node
    if (!menu || !e.target || typeof menu.contains !== 'function' || !menu.contains(e.target))
      return
    el.call('menuArrowNav', e, menu, { selector: '[role="menuitem"]' })
  },

  // ── CONTEXT — what the ask is about.
  PanelContext: {
    flow: 'y',
    gap: 'Y',
    minWidth: '0',
    AiPanelLabel: { text: (el) => el.call('polyglot', 'ai.scope.label') },
    AiScopeSwitch: {}
  },

  // ── THE THREE VERBS — one symmetric row: three equal tracks.
  PanelActions: {
    role: 'group',
    ariaLabel: (el) => el.call('polyglot', 'ai.panel.actions'),
    if: (el, s) => el.parent.scope.actions(el.parent, s).length > 0,
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: 'Y',
    minWidth: '0',
    childExtends: 'AiPanelAction',
    childrenAs: 'state',
    children: (el, s) =>
      // the element key is prefixed (never a bare word a tag or a prop could
      // own); the verb itself rides as `verb` — the one the click runs
      el.parent.scope
        .actions(el.parent, s)
        .map((a) => ({ ...a, verb: a.key, key: 'verb-' + a.key })),
    // ← → between the verbs (the row is one Tab stop per verb; the arrows are
    // a convenience, the menu's own walker on the horizontal axis).
    onKeydown: (e, el) =>
      el.call('menuArrowNav', e, el.node, { selector: 'button', orientation: 'x' })
  },

  // ── THE CONTEXT'S ASKS — a menu of grouped rows (the house menu row).
  // Hidden while the member is typing: the starters are for an empty field;
  // the context and the verbs stay.
  PanelMenu: {
    role: 'menu',
    tabindex: '-1',
    ariaLabel: (el) => el.call('polyglot', 'ai.panel.suggestions'),
    hide: (el, s) => !!String((s && s.draft) || '').trim(),
    flow: 'y',
    gap: 'Z',
    minWidth: '0',
    childExtends: 'AiPanelGroup',
    childrenAs: 'state',
    // A group's key carries the context and its rows' words, so a change
    // re-mounts the group instead of patching rows in place.
    children: (el, s) => {
      const panel = el.parent
      const scopeKey = panel.scope.scopeKey(panel, s)
      return panel.scope.groups(panel, s).map((g) => ({
        key: g.key + ':' + scopeKey + ':' + panel.scope.sig(g.rows),
        label: g.label,
        rows: g.rows || []
      }))
    }
  },

  // ── THE MODE — what the AI makes; the current one named with what it does.
  PanelMode: {
    flow: 'y',
    gap: 'Y',
    minWidth: '0',
    AiPanelLabel: { text: (el) => el.call('polyglot', 'ai.assistantMode.label') },
    AiModeSwitch: {}
  }
}
